<template>
  <loading-content :loading="loading">
    <div :class="{ 'obj-card': plate }">
    <!-- The new look: a card whose head says how many there are and holds the
         two buttons the floating "+" and the bar above do in the old one. -->
    <div v-if="plate" class="obj-card__head obj-card__head--flat">
      <h2>Märkused <small>· {{ count }}</small></h2>
      <router-link :to="{ name: 'object/notes/print', params: { objectId } }" class="obj-btn obj-btn--outline obj-btn--sm">
        <v-icon size="16">mdi-printer</v-icon>Prindi ajalugu
      </router-link>
      <button type="button" class="obj-btn obj-btn--sm" @click="newForm = true">
        <v-icon size="16">mdi-plus</v-icon>Lisa märkus
      </button>
    </div>
    <div v-else class="d-flex justify-end mb-2">
      <!-- "ajalugu": this prints the whole list. Each entry below has its own
           Prindi, which is the one-page document for that entry alone. -->
      <v-btn v-if="embedded" class="mr-2" color="primary" @click="newForm = true" size="small" variant="outlined">
        <v-icon size="small" start>mdi-plus</v-icon>Lisa märkus
      </v-btn>
      <v-btn v-if="embedded" color="primary" :href="printHref()" target="_blank" size="small" variant="outlined">
        <v-icon size="small" start>mdi-printer</v-icon>Prindi ajalugu
      </v-btn>
      <v-btn v-else color="primary" :to="{ name: 'object/notes/print', params: { objectId } }" size="small" variant="outlined">
        <v-icon size="small" start>mdi-printer</v-icon>Prindi ajalugu
      </v-btn>
    </div>
    <template v-for="item in notes" :key="item.type + item.id">
      <!-- Date, who and what kind on the left; the text, with a hooldus's steps
           in two columns and the first four shown; what can be done with it on
           the right. The same actions as the old row, worded the same. -->
      <article v-if="plate" class="obj-note">
        <div class="obj-note__side">
          <span class="obj-note__date">{{ day(item.entryDate) }}</span>
          <span class="obj-note__who">{{ (item.creator && item.creator.fullName) || '—' }}</span>
          <span class="obj-kind" :class="item.type === 'calendar' ? 'obj-kind--event' : item.service ? 'obj-kind--service' : 'obj-kind--note'">
            {{ item.type === 'calendar' ? 'Töökäsk' : item.service ? 'Hooldus' : 'Märkus' }}
          </span>
          <span v-if="item.interval" class="obj-note__sub">Tähtaeg {{ item.interval.title }}</span>
          <a v-if="item.calendar" href="#" class="obj-link" @click.prevent="viewEvent(item.calendar)">{{ eventPeriod(item.calendar) }}</a>
        </div>
        <div class="obj-note__body">
          <span v-if="parsed(item).comment" class="obj-note__text">{{ parsed(item).comment }}</span>
          <template v-if="parsed(item).works.length">
            <span class="obj-cap mt-1">LÄBIVIIDUD HOOLDUSTÖÖD</span>
            <ol class="obj-steps">
              <li v-for="(work, i) in shownWorks(item)" :key="i">{{ work }}</li>
            </ol>
            <button v-if="parsed(item).works.length > STEPS_SHOWN" type="button" class="obj-link" @click="toggleSteps(item)">
              {{ openSteps[item.id] ? 'Näita vähem' : 'Näita kõiki ' + parsed(item).works.length + ' tööd' }}
            </button>
          </template>
          <div v-if="(item.photos || []).length" class="obj-note__photos">
            <v-img
              v-for="(pic, i) in item.photos"
              :key="i"
              width="96"
              height="96"
              style="flex: 0 0 auto; border-radius: 6px; cursor: pointer"
              aspect-ratio="1"
              :src="errored[item.id + '-' + pic] ? logo : photoUrl(item.id, pic)"
              :cover="!errored[item.id + '-' + pic]"
              :class="{ 'bg-grey-lighten-3': errored[item.id + '-' + pic] }"
              @error="onError(item.id, pic)"
              @click="openPhoto(item.id, pic)"
            />
          </div>
        </div>
        <div class="obj-note__actions">
          <span class="obj-pill obj-pill--sm" :class="item.hiddenFromClient ? 'obj-pill--grey' : item.photosHiddenFromClient ? 'obj-pill--late' : 'obj-pill--ok'">
            {{ item.hiddenFromClient ? 'Kliendile peidetud' : item.photosHiddenFromClient ? 'Fotod peidetud' : 'Kliendile nähtav' }}
          </span>
          <template v-if="item.type === 'note'">
            <button type="button" class="obj-link obj-link--muted" :disabled="saving[item.id]" @click="setVisibility(item, { hiddenFromClient: !item.hiddenFromClient })">
              {{ item.hiddenFromClient ? 'Näita kliendile' : 'Peida kliendi eest' }}
            </button>
            <button v-if="!item.hiddenFromClient && (item.photos || []).length" type="button" class="obj-link obj-link--muted" :disabled="saving[item.id]" @click="setVisibility(item, { photosHiddenFromClient: !item.photosHiddenFromClient })">
              {{ item.photosHiddenFromClient ? 'Näita fotosid' : 'Peida fotod' }}
            </button>
            <span v-if="canIssueDoc(item)" class="obj-pair">
              <a :href="hooldusaktUrl(item)" target="_blank" class="obj-link">Prindi</a>
              <button type="button" class="obj-link" @click="openSend(item)">Saada</button>
            </span>
            <button v-if="canDelete" type="button" class="obj-link obj-link--danger" :disabled="saving[item.id]" @click="confirmDelete = item">Kustuta</button>
          </template>
          <button v-else-if="item.type === 'calendar'" type="button" class="obj-link obj-link--muted" :disabled="saving[item.id]" @click="setWorkOrderVisibility(item, { hiddenFromClient: !item.hiddenFromClient })">
            {{ item.hiddenFromClient ? 'Näita kliendile' : 'Peida kliendi eest' }}
          </button>
        </div>
      </article>
      <template v-else>
      <div :class="compact ? 'note-compact py-3' : 'd-sm-flex pa-3'">
        <div class="note-side pr-5">
          <!-- On a phone, in the Töökäsk popup: who and when on one line, what
               kind of entry under it, then the text itself — the five label
               lines below would push the note off the first screen. -->
          <div v-if="compact" class="note-meta">
            <div class="d-flex flex-wrap align-baseline">
              <strong class="mr-2">{{(item.creator && item.creator.fullName) || '—'}}</strong>
              <span class="text-caption">{{format(item.entryDate)}}</span>
            </div>
            <div class="text-caption text-medium-emphasis">
              {{type(item.type)}}<template v-if="item.service"> · hooldus</template><template v-if="item.interval"> · {{item.interval.title}}</template>
              <template v-if="item.calendar"> · <a href="#" @click="viewEvent(item.calendar)">{{eventPeriod(item.calendar)}}</a></template>
            </div>
          </div>
          <div v-else class="note-meta">
            <div class="no-wrap"><strong>{{(item.creator && item.creator.fullName) || '—'}}</strong></div>
            <div class="no-wrap text-caption">
              Tüüp: {{type(item.type)}}
              <a href="#" @click="viewEvent(item.calendar)" v-if="item.calendar">{{eventPeriod(item.calendar)}}</a>
            </div>
            <div class="no-wrap text-caption">Kuupäev: {{format(item.entryDate)}}</div>
            <div class="no-wrap text-caption">Hooldus: {{item.service ? 'Jah' : 'Ei'}}</div>
            <div class="no-wrap text-caption" v-if="item.interval">Intervall: {{item.interval.title}}</div>
            <div class="no-wrap text-caption">Loodud: {{item.dateCreated}}</div>
          </div>
          <div class="note-actions">
          <template v-if="item.type === 'note'">
            <div class="mt-1">
              <v-chip v-if="item.hiddenFromClient" color="grey" size="x-small" variant="flat">Kliendile peidetud</v-chip>
              <v-chip v-else-if="item.photosHiddenFromClient" color="orange-lighten-4" size="x-small" variant="flat">Fotod peidetud</v-chip>
              <v-chip v-else color="green-lighten-4" size="x-small" variant="flat">Kliendile nähtav</v-chip>
            </div>
            <div class="mt-1">
              <v-btn class="px-1" :loading="saving[item.id]" @click="setVisibility(item, { hiddenFromClient: !item.hiddenFromClient })" size="x-small" variant="text">
                <v-icon size="x-small" start>{{item.hiddenFromClient ? 'mdi-eye' : 'mdi-eye-off'}}</v-icon>
                {{item.hiddenFromClient ? 'Näita kliendile' : 'Peida kliendi eest'}}
              </v-btn>
            </div>
            <div v-if="!item.hiddenFromClient && (item.photos || []).length">
              <v-btn class="px-1" :loading="saving[item.id]" @click="setVisibility(item, { photosHiddenFromClient: !item.photosHiddenFromClient })" size="x-small" variant="text">
                <v-icon size="x-small" start>{{item.photosHiddenFromClient ? 'mdi-image' : 'mdi-image-off'}}</v-icon>
                {{item.photosHiddenFromClient ? 'Näita fotosid' : 'Peida fotod'}}
              </v-btn>
            </div>
            <!-- A hooldus comes out as a hooldusakt and anything else as a
                 märkus, but either only means something on a contract object —
                 which is exactly what the server will accept. -->
            <div v-if="canIssueDoc(item)" class="mt-1">
              <v-btn class="px-1" :href="hooldusaktUrl(item)" target="_blank" size="x-small" variant="text">
                <v-icon size="x-small" start>mdi-printer</v-icon>Prindi
              </v-btn>
              <v-btn class="px-1" @click="openSend(item)" size="x-small" variant="text">
                <v-icon size="x-small" start>mdi-email-outline</v-icon>Saada
              </v-btn>
            </div>
            <!-- Root only, and only on a real note: a calendar row in this list
                 carries a *calendar* id in item.id (all_notes reuses the
                 column), so a delete here would aim at the wrong record. -->
            <div v-if="canDelete" class="mt-1">
              <v-btn class="px-1" color="red-darken-2"
                :loading="saving[item.id]" @click="confirmDelete = item" size="x-small" variant="text">
                <v-icon size="x-small" start>mdi-delete-outline</v-icon>Kustuta
              </v-btn>
            </div>
          </template>
          <template v-else-if="item.type === 'calendar'">
            <div class="mt-1">
              <v-chip v-if="item.hiddenFromClient" color="grey" size="x-small" variant="flat">Kliendile peidetud</v-chip>
              <v-chip v-else color="green-lighten-4" size="x-small" variant="flat">Kliendile nähtav</v-chip>
            </div>
            <div class="mt-1">
              <v-btn class="px-1" :loading="saving[item.id]" @click="setWorkOrderVisibility(item, { hiddenFromClient: !item.hiddenFromClient })" size="x-small" variant="text">
                <v-icon size="x-small" start>{{item.hiddenFromClient ? 'mdi-eye' : 'mdi-eye-off'}}</v-icon>
                {{item.hiddenFromClient ? 'Näita kliendile' : 'Peida kliendi eest'}}
              </v-btn>
            </div>
          </template>
          </div>
        </div>
        <div class="notes">
          <span v-html="safe(item.description)" />
          <div class="d-flex flex-wrap mt-2">
            <v-img
              v-for="(pic, i) in (item.photos || [])"
              :key="i"
              :width="compact ? 96 : 150"
              :height="compact ? 96 : 150"
              class="mr-2 mb-2"
              style="flex: 0 0 auto"
              aspect-ratio="1"
              :src="errored[item.id + '-' + pic] ? logo : photoUrl(item.id, pic)"
              :cover="!errored[item.id + '-' + pic]"
              :class="{ 'bg-grey-lighten-3': errored[item.id + '-' + pic] }"
              @error="onError(item.id, pic)"
              @click="openPhoto(item.id, pic)"
            />
          </div>
        </div>
      </div>
      <v-divider />
      </template>
    </template>
    <div v-if="notes.length <= 0 && !loading" :class="plate ? 'obj-empty' : 'text-grey'">Märkused puuduvad</div>
    </div>
    <v-pagination v-if="notes.length" class="mt-5 mb-5" v-model="page" :length="pages" :total-visible="7" />
    <note-form :object="object" :objectId="objectId" v-model:visible="newForm" @created="refresh()" />
    <!-- Centred, as Vuetify 2's overlay drew its content by itself. A click
         beside the photo or Esc closes it too, which is Vuetify 3's own way. -->
    <v-overlay
      :model-value="!!overlay"
      class="align-center justify-center"
      @update:model-value="open => open || (overlay = false)"
    >
      <div class="text-center" style="width: 100vw">
        <v-btn icon variant="text" color="white" @click="overlay = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <img v-if="overlay" @click="overlay = false" :src="overlay" style="max-width:90%;max-height:88vh;object-fit:contain;margin:auto;display:block;" />
      </div>
    </v-overlay>
    <!-- Vuetify 2's `fab fixed bottom right`: a 64px round button 16px in from
         the corner of the window. -->
    <v-fab-transition v-if="!embedded && !plate">
      <v-btn
        icon
        color="primary"
        size="x-large"
        style="position: fixed; bottom: 16px; right: 16px; z-index: 10"
        @click="newForm = true"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-dialog v-model="selectedEvent" max-width="700px">
      <event mode="view" :eventId="selectedEventId" @close="selectedEvent = false" />
    </v-dialog>
    <v-dialog v-model="sendDialog" max-width="480px">
      <v-card>
        <v-card-title class="text-h6">Saada {{docWord(sendNote)}}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="sendEmail"
            label="E-posti aadress"
            type="email"
            autofocus
            :disabled="sending"
            @keyup.enter="confirmSend"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="sending" @click="sendDialog = false" variant="text">Loobu</v-btn>
          <v-btn color="primary" :loading="sending" :disabled="!sendEmail" @click="confirmSend">Saada</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <confirm
      :visible="!!confirmDelete"
      :title="confirmDelete && confirmDelete.service ? 'Kustuta hooldusmärge?' : 'Kustuta märkus?'"
      :action="confirmDelete && confirmDelete.service ? 'Kustuta märge' : 'Kustuta märkus'"
      :detail="confirmDelete ? (confirmDelete.service ? 'Hooldus ' : 'Märkus ') + day(confirmDelete.entryDate) + ' · ' + ((confirmDelete.creator && confirmDelete.creator.fullName) || '—') : ''"
      @confirm="remove(confirmDelete)"
      @cancel="confirmDelete = null"
    >
      Märkus ja selle fotod kustutatakse jäädavalt.
      <!-- Signing an akt files its field photos as a märkus, and the two name
           the same file rather than each keeping a copy. The server checks that
           and hides the note instead of erasing it when an akt still needs the
           photos — it says so in the answer, so the warning here only has to
           prepare the reader for it. -->
      <v-alert v-if="(confirmDelete && confirmDelete.photos || []).length" type="info" class="mt-3 mb-0" variant="tonal" density="compact">
        Kui fotod on kasutusel mõnel aktil, siis märkus peidetakse ja fotod jäävad alles.
      </v-alert>
      <v-alert v-if="confirmDelete && confirmDelete.interval" type="warning" class="mt-3 mb-0" variant="tonal" density="compact">
        Sellega läheb hooldus <strong>{{confirmDelete.interval.title}}</strong> uuesti tegemata olekusse.
      </v-alert>
    </confirm>
  </loading-content>
</template>

<script>
import api from '@/api'
import { mapActions } from 'vuex'
import { pickBy } from 'lodash'
import dayjs from 'dayjs'
import helpersMixin from '@/plugins/helpersMixin'
import { parseDescription } from '@/plugins/utils'
import logo from '@/assets/gaspre-logo.png'
import { sanitizeHtml } from '@/plugins/sanitize'

export default {
  mixins: [ helpersMixin ],
  props: {
    object: {
      required: true
    },
    // A prop rather than read off the route, so the Töökäsk popup can show
    // this same list over the calendar. The object page passes it through its
    // <router-view>.
    objectId: {
      type: [String, Number],
      default: null
    },
    // Inside a dialog (Event.vue): the fixed "+" would sit behind the dialog's
    // scrim, and Prindi ajalugu would navigate away from a half-filled work
    // order, so both change shape.
    embedded: {
      type: Boolean,
      default: false
    },
    // The object page's new look (Seaded -> Objekt uues kujunduses).
    plate: {
      type: Boolean,
      default: false
    },
    overview: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      selectedEvent: false,
      selectedEventId: null,
      overlay: false,
      newForm: false,
      // Which hooldus entries show every step, in the new look.
      openSteps: {},
      STEPS_SHOWN: 4,
      errored: {},
      saving: {},
      sendDialog: false,
      sendEmail: '',
      sendNote: null,
      sending: false,
      confirmDelete: null,
      logo,
      notes: [],
      count: 0,
      page: 1,
      itemsPerPage: 15,
      loading: true,
      pagination: {},
      headers: [{
        title: 'Info',
        value: 'creator',
        sortable: false,
        align: 'start'
      }, {
        title: 'Kirjeldus',
        value: 'description',
        sortable: false,
        align: 'start'
      }, {
        title: '',
        value: 'actions',
        sortable: false,
        align: 'end'
      }]
    }
  },
  watch: {
    // "Lisa märkus" in the object page's head comes here with ?lisa=1.
    '$route.query.lisa': {
      handler (value) {
        if (value && !this.embedded) this.newForm = true
      },
      immediate: true
    },
    // ?lisa leaves the address only once the form is closed: Vuetify closes an
    // open dialog on any navigation, so dropping it while the form opened shut
    // the form again (a pasted .../notes?lisa=1 link showed no form).
    newForm (open) {
      if (!open && this.$route.query.lisa) {
        this.$router.replace({ query: { ...this.$route.query, lisa: undefined } }).catch(() => {})
      }
    },
    params: {
      handler() {
        this.refresh()
      },
      deep: true
    }
  },
  emits: ['changed'],
  computed: {
    // The popup on a phone-sized screen. The object page keeps its own layout
    // at every width.
    compact () {
      return this.embedded && this.$vuetify.display.xs
    },
    // Root only. A märkus is the object's memory of a visit and nothing else
    // can reconstruct it, so it is not an ordinary admin action.
    canDelete () {
      return this.$hasRole(['root'])
    },
    params () {
      return pickBy({
        limit: this.itemsPerPage,
        offset: (this.page - 1) * this.itemsPerPage,
        ...this.search
      }, v => v !== undefined)
    },
    pages () {
      return Math.ceil(this.count / this.itemsPerPage)
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    printHref () {
      return this.$router.resolve({ name: 'object/notes/print', params: { objectId: this.objectId } }).href
    },
    async refresh () {
      // Leaving the page clears the route's objectId before this tab is gone.
      if (!this.objectId) {
        return
      }
      this.loading = true
      this.errored = {}
      try {
        const { notes, count } = await api.getObjectNotes(this.objectId, this.params)
        this.notes = notes
        this.count = count
        this.$emit('changed', count)
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.loading = false
    },
    // The server decides between erasing and hiding — it is the only side that
    // can see whether an akt still lists these photos — so the toast reports
    // what it actually did rather than what the button asked for.
    async remove (item) {
      this.confirmDelete = null
      if (!item) {
        return
      }
      this.saving[item.id] = true
      try {
        const { deleted, aktId } = await api.deleteNote(this.objectId, item.id)
        this.showNotification(deleted === 'hidden'
          ? {
            text: 'Märkus peideti — fotod on kasutusel aktil nr ' + aktId + ' ja jäid alles',
            color: 'orange-darken-2'
          }
          : { text: 'Märkus kustutatud', color: 'green' })
        await this.refresh()
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.saving[item.id] = false
    },
    async setVisibility (item, changes) {
      this.saving[item.id] = true
      try {
        const { note } = await api.setNoteVisibility(this.objectId, item.id, changes)
        item.hiddenFromClient = note.hiddenFromClient
        item.photosHiddenFromClient = note.photosHiddenFromClient
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.saving[item.id] = false
    },
    // A document is issued per note, and only on a contract object — the same
    // condition the server enforces, so the buttons are only offered where they
    // would actually work. A töökäsk row is a calendar entry, not a note, and
    // has nothing to issue.
    canIssueDoc (item) {
      return item.type === 'note' && this.object.type === this.objectTypes.CONTRACT
    },
    // The client-side twin of documentKind() in service/hooldusaktPdf.js: what
    // the buttons and the toast should call what is about to be sent.
    docWord (item) {
      return (item && item.service) ? 'hooldusakt' : 'märkus'
    },
    hooldusaktUrl (item) {
      return api.hooldusaktUrl(this.objectId, item.id)
    },
    openSend (item) {
      this.sendNote = item
      // The client's own address is the usual answer; it stays editable because
      // acts are often wanted at a manager's or accountant's address instead.
      this.sendEmail = (this.object.client && this.object.client.email) || ''
      this.sendDialog = true
    },
    async confirmSend () {
      if (!this.sendEmail || this.sending) {
        return
      }
      this.sending = true
      try {
        const word = this.docWord(this.sendNote)
        const { sent } = await api.sendHooldusakt(this.objectId, this.sendNote.id, this.sendEmail)
        this.showNotification({ text: word[0].toUpperCase() + word.slice(1) + ' saadetud: ' + sent, color: 'green' })
        this.sendDialog = false
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.sending = false
    },
    // Töökäsk rows are calendar entries, not notes — their visibility lives on the calendar
    // row and drives the customer's Töökäsud tab. item.id is the calendar id for these rows.
    async setWorkOrderVisibility (item, changes) {
      this.saving[item.id] = true
      try {
        const { workorder } = await api.setWorkOrderVisibility(this.objectId, item.id, changes)
        item.hiddenFromClient = workorder.hiddenFromClient
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.saving[item.id] = false
    },
    safe: sanitizeHtml,
    // A hooldus entry split into its comment and its steps, the way the
    // hooldusakt prints it; anything else is all comment.
    parsed (item) {
      return parseDescription(item.description)
    },
    shownWorks (item) {
      const works = this.parsed(item).works
      return this.openSteps[item.id] ? works : works.slice(0, this.STEPS_SHOWN)
    },
    toggleSteps (item) {
      this.openSteps[item.id] = !this.openSteps[item.id]
    },
    day (date) {
      return dayjs(date).format('DD.MM.YYYY')
    },
    format (date) {
      return dayjs(date).format('YYYY-MM-DD')
    },
    photoUrl (noteId, picture) {
      return '/api/object/' + this.objectId + '/note/' + noteId + '/photo?picture=' + picture
    },
    onError (noteId, picture) {
      this.errored[noteId + '-' + picture] = true
    },
    openPhoto (noteId, picture) {
      if (this.errored[noteId + '-' + picture]) {
        return
      }
      this.overlay = this.photoUrl(noteId, picture)
    },
    type (key) {
      switch (key) {
        case 'calendar':
          return 'töökäsk'
        default:
          return 'märkus'
      }
    },
    viewEvent (event) {
      this.selectedEventId = event.id
      this.selectedEvent = true
    }
  },
  mounted () {
    this.refresh()
  }
}
</script>

<style lang="scss">
/* The meta, the text and the actions stacked in that order: the side column
   steps out of the way (display: contents) so its two halves can sit either
   side of the note text, and the chip and buttons share wrapped rows instead
   of a line each. */
.note-compact {
  display: flex;
  flex-direction: column;
  > .note-side {
    display: contents;
  }
  .note-meta {
    order: 0;
  }
  .notes {
    order: 1;
    margin-top: 4px;
  }
  .note-actions {
    order: 2;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 4px;
    margin-top: 4px;
    > div {
      margin-top: 0 !important;
    }
    /* x-small is 20px: fine for a mouse, too small for a thumb. */
    .v-btn {
      height: 32px;
    }
  }
}
.notes {
  i {
    display: block;
  }
  b.title {
    font-size: 15px !important;
    margin: 0 !important;
    padding: 0 !important;
    display: block;
  }
  p {
    margin-bottom: 0px !important;
    margin-top: 0px !important;
  }
  br {
    display: none;
  }
}
</style>